import React, { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import DynamicTable from "../../components/Table/DataTable";
import TablePaginationActions from "../../components/Table/tablePagination";
import { IconCancel, IconEdit, IconEye } from "../../components/icons/icons";
import apiClient from "../../services/apiClient";
import moment from "moment";

const ListView = ({
  appointments,
  page,
  rowsPerPage,
  loading,
  error,
  totalCount,
  handleListViewClick,
  handlePageChange,
  handleRowsPerPageChange,
  handleEditClick,
  handleRefundClick,
  fetchAppointments,
}: any) => {
  const [parentNames, setParentNames] = useState<{ [petId: string]: string }>(
    {}
  );
  const [parentId, setParentId] = useState("");
  // Fetch parent info for each unique petId
  useEffect(() => {
    const fetchParentNames = async () => {
      const petIds = appointments
        .map(
          (row: any) =>
            row.participants?.find((p: any) => p.role === "subscriber")
              ?.ref_number
        )
        .filter(
          (id: string | null | undefined): id is string =>
            !!id && !parentNames[id]
        );

      const uniquePetIds = Array.from(new Set(petIds));

      for (const petId of uniquePetIds) {
        try {
          const petRes: any = await apiClient(`v1/resource/pets?id=${petId}`, {
            method: "GET",
          });
          const pets = petRes?.data?.pets ?? [];
          let primary_id: number | null = null;

          if (pets.length > 0) {
            const { primary_id: id } = pets[0];
            primary_id = id;
          }
          setParentId(primary_id?.toString() || "");
          const parentId = primary_id;

          if (parentId) {
            const patientRes: any = await apiClient(
              `v1/resource/patients?id=${parentId}`,
              {
                method: "GET",
              }
            );
            const parent = patientRes?.data?.patients ?? [];
            const user = parent[0]?.user;
            const fullName = `${user?.first_name ?? ""} ${
              user?.last_name ?? ""
            }`.trim();
            setParentNames((prev) => ({
              ...prev,
              [petId as string]: fullName || "N/A",
            }));
          }
        } catch (err) {
          console.error(`Error fetching parent for petId ${petId}`, err);
        }
      }
    };

    if (appointments?.length > 0) fetchParentNames();
  }, [appointments, parentNames]);

  const getDisplayStatus = (name?: string) => {
    if (!name) return "-";
    const statusDisplayMap: Record<string, string> = {
      "Cancelled (You)": "Cancelled by owner",
      // Add more mappings if needed
    };
    return statusDisplayMap[name] || name;
  };
  const getRowStyle = (row: any) => {
    if (
      row?.reason === "Emergency Consult" ||
      row?.reason === "Emergency consultation"
    ) {
      return {
        backgroundColor: "#fee2e2",
      };
    }
    return {};
  };

  const getStatusClass = (slug?: string) => {
    if (!slug) return "bg-gray-500";
    const key = slug.toLowerCase();

    const statusMap: Record<string, string> = {
      new: "bg-blue-600",
      waiting: "bg-amber-500",
      unpaid: "bg-orange-500",
      ended: "bg-slate-600",
      started: "bg-indigo-600",
      inprogress: "bg-indigo-700",
      cancelled: "bg-red-600",
      "cancelled (you)": "bg-red-600",
      "cancelled (vet)": "bg-red-600",
      completed: "bg-green-600",
      confirmed: "bg-emerald-600",
      paid: "bg-teal-600",
      pending: "bg-amber-500",
      "payment pending": "bg-amber-600",
      accepted: "bg-purple-600",
      "vet accepted": "bg-purple-600",
      "vet joined": "bg-violet-600",
      "joined (you)": "bg-violet-700",
      "reschedule requested": "bg-cyan-500",
      rescheduled: "bg-cyan-700",
      "reschedule rejected": "bg-cyan-900",
      expired: "bg-red-500",
      "no show (you)": "bg-rose-500",
      "no show (vet)": "bg-rose-600",
      "refund pending": "bg-yellow-500",
      refunded: "bg-green-500",
      "refund failed": "bg-red-700",
      "refund initiated": "bg-teal-500",
      created: "bg-slate-500",
      "link ready": "bg-sky-500",
    };

    return statusMap[key] || "bg-gray-500";
  };

  const renderers = (row: any, index?: number) => {
    const Pet = row.participants?.find((p: any) => p.role === "subscriber");
    const Vet = row.participants?.find((p: any) => p.role === "publisher");
    const consultTypeMap: Record<string, string> = {
      virtual: "Teleconsult",
      home: "Home Visit",
      clinic: "Walk In",
    };

    const Type = consultTypeMap[row.consult_type] || row.consult_type;

    const petId = Pet?.ref_number;
    const parentName = petId ? parentNames[petId] : "Loading...";

    return {
      serial: page * rowsPerPage + (index ?? 0) + 1,
      PetName: (
        <Typography variant="caption">{Pet?.participant_info?.name}</Typography>
      ),
      Vetenerian: (
        <Typography variant="caption">{Vet?.participant_info?.name}</Typography>
      ),
      DateTime: row?.scheduled_at
        ? moment(row.scheduled_at).format("DD-MM-YYYY hh:mm A")
        : "N/A",
      speciality:
        Vet?.participant_info?.additional_info?.consult_speciality ?? "N/A",
      Parent: <Typography variant="caption">{parentName || "N/A"}</Typography>,
      Type: <Typography variant="caption">{Type}</Typography>, // ✅ Added here
      status: (
        // <span
        //     className={`text-white text-xs px-2 py-1 rounded ${getStatusClass(
        //         row.consult_current_status?.name
        //     )}`}
        // >
        <span
          className={`text-white text-xs px-2 py-1 rounded ${getStatusClass(
            row.consult_current_status?.name
          )}`}
        >
          {/* {row.consult_current_status?.name || '-'} */}
          {getDisplayStatus(row.consult_current_status?.name)}
        </span>
      ),
    };
  };

  const columns = [
    {
      key: "serial",
      label: "S.No",
      align: "Left",
      render: (_row: any, index: number) => renderers(_row, index).serial,
    },
    { key: "id", label: "ID", align: "Left" },
    {
      key: "Vetenerian",
      label: "Veterinarian",
      align: "Left",
      render: (row: any) => renderers(row).Vetenerian,
    },
    {
      key: "Parent",
      label: "Parent Name",
      align: "Left",
      render: (row: any) => renderers(row).Parent,
    },
    {
      key: "PetName",
      label: "Pet Name",
      align: "Left",
      render: (row: any) => renderers(row).PetName,
    },
    {
      key: "Type",
      label: "Type",
      align: "center",
      render: (row: any) => renderers(row).Type,
    },
    {
      key: "DateTime",
      label: "Date & Time",
      align: "Left",
      render: (row: any) => renderers(row).DateTime,
    },
    {
      key: "status",
      label: "Status",
      align: "Left",
      render: (row: any) => renderers(row).status,
    },
  ];

  const actions = [
    {
      label: "View",
      color: "#00bcd4",
      icon: <IconEye size={20} />,
      onClick: (row: any) => handleListViewClick(row),
    },
    // {
    //     label: 'Review',
    //     color: '#9c27b0',
    //     icon: <IconReview size={20} />,
    //     onClick: (row: any) => handleListViewClick(row),
    // },
    {
      label: "Edit",
      color: "#4caf50",
      icon: <IconEdit size={20} />,
      onClick: (row: any) => handleEditClick(row, parentId),
    },
    {
      label: "Cancel",
      color: "#f44336",
      icon: <IconCancel size={20} />,
      onClick: async (row: any) => {
        const confirmCancel = window.confirm(
          "Are you sure you want to cancel this action?"
        );
        if (confirmCancel) {
          try {
            await apiClient<any>(`v1/resource/consults/${row.id}`, {
              method: "PUT",
              body: {
                id: row?.id,
                status: "cancelled",
              },
            });
            fetchAppointments();
          } catch (error) {
            console.error("Failed to update status:", error);
            alert("Failed to cancel the appointment. Please try again.");
          }
        }
      },
    },
    // {
    //     label: 'Refund',
    //     color: '#e91e63',
    //     icon: <IconRefund size={20} />,
    //     onClick: (row: any) => handleRefundClick(row),
    // },
  ];

  return (
    <DynamicTable
      columns={columns}
      data={appointments}
      loading={loading}
      actions={actions}
      page={page}
      error={error}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      ActionsComponent={TablePaginationActions}
      getRowStyle={getRowStyle}
    />
  );
};

export default ListView;
